import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { EMPTY, Observable, of } from "rxjs";
import { catchError, delay, expand, map, switchMap } from "rxjs/operators";
import { ISignedURL } from "src/app/model/custom/ISignedURL";
import { environment } from "src/environments/environment";
import { ImageSizePrefix } from "../../model/custom/IImage";
import { IInductionPage } from "../../model/custom/IInductionPage";
import { SearchImagesModalComponent } from "../modals/search-images-modal/search-images-modal.component";
import { cloneDeep } from "./utils";

@Injectable({
	providedIn: "root",
})
export class ImageService {
	private http: HttpClient = inject(HttpClient);
	private dialog: MatDialog = inject(MatDialog);

	readonly hardResetTimerLimit = 60 * 1000;

	_resizeImage(s3URL) {
		return this.http.put(`${environment.apiURL}/image-v2/resize`, {
			s3URL,
		});
	}
	AiGenerateImage(html) {
		return this.http.put(`${environment.apiURL}/company/generate-image`, {
			html,
		});
	}

	ExpectedImageURLForWidth(urlOrig: string, width: number) {
		let url = `${urlOrig}`;
		if (url.indexOf("http://") === 0) {
			url = url.replace("http://", "https://");
		}
		if (url.indexOf("//") === 0) {
			url = url.replace("//", "https://");
		}

		const ext = url.substring(url.lastIndexOf("."));
		if (![".jpg", ".png", ".jpeg"].includes(ext)) {
			return url;
		}
		const replaceExt = new RegExp(ext + "$");
		let expectedExtension;

		if (width < 768) {
			expectedExtension = "-xs" + ext;
		} else if (width < 992) {
			expectedExtension = "-sm" + ext;
		} else if (width < 1300) {
			expectedExtension = "-md" + ext;
		} else {
			expectedExtension = "-lg" + ext;
		}

		if (url.indexOf(expectedExtension) > -1) {
			return url;
		}
		url = url.replace(replaceExt, expectedExtension);

		return url;
	}

	GetCloudFrontURL(url: string) {
		if (
			!url.startsWith("https://") ||
			url.indexOf(environment.cloudFrontContentURL.replace("https://", "")) > 0
		) {
			return url;
		}
		return url;
	}

	ReplaceImageInHTML(htmlOrig) {
		const images = [];
		let html = `${htmlOrig}`;
		if (!htmlOrig || htmlOrig.length === 0) {
			return htmlOrig;
		}
		const windowWidth = window.innerWidth;
		var div = document.createElement("div");
		div.innerHTML = html;
		const allImages = Array.from(div.getElementsByTagName("img"));
		for (const elm of allImages) {
			const src = elm.getAttribute("src");
			const width = Number(windowWidth);
			if (src && src.length) {
				const replaceWith = this.ExpectedImageURLForWidth(src, width);
				const cloudFrontURL = this.GetCloudFrontURL(replaceWith);
				elm.className += " img-fluid";
				if (src !== cloudFrontURL) {
					elm.setAttribute("src", cloudFrontURL);
					images.push(src);
				}
			}
		}

		return div.innerHTML;
	}

	ReplaceImageInPages(pages: IInductionPage[]) {
		// check if pages is an array
		if (!Array.isArray(pages)) {
			return [];
		}
		return pages.map((page) => {
			const pageCopy = cloneDeep(page) as IInductionPage;
			pageCopy.Information = this.ReplaceImageInHTML(pageCopy.Information);
			pageCopy.PageQuestion = this.ReplaceImageInHTML(pageCopy.PageQuestion);
			return pageCopy;
		});
	}

	RotateImage(
		url,
		action,
		size: ImageSizePrefix = "sm",
	): Observable<{ key: string; link: string }> {
		let timeCountFlag = 0;
		return this.http
			.put(`${environment.apiURL}/image-v2/${action}`, {
				s3URL: url,
			})
			.pipe(
				delay(10 * 1000),
				switchMap((result: { key: string; link: string }) => {
					const checkStatus = () => {
						timeCountFlag += 20 * 1000;

						return this.http
							.put(`${environment.apiURL}/image-v2/exists`, {
								s3URL: this.AddSizePrefix(result.link, size),
							})
							.pipe(
								map((i) => of(i)),
								catchError(() => of(false)),
							);
					};
					return checkStatus().pipe(
						expand((res: boolean) => {
							if (res || timeCountFlag >= this.hardResetTimerLimit) {
								return EMPTY;
							}
							return checkStatus().pipe(delay(5 * 1000));
						}),
						switchMap(() => {
							return of(result);
						}),
					);
				}),
			);
	}

	ResizeImage(
		result: {
			fileName: string;
			fileType: string;
			link: string;
			publicLink: string;
			key: string;
		},
		size: ImageSizePrefix = "sm",
	) {
		let timeCountFlag = 0;
		return this._resizeImage(result.link).pipe(
			delay(10 * 1000),
			switchMap(() => {
				const checkStatus = () => {
					timeCountFlag += 20 * 1000;

					return this.http
						.put(`${environment.apiURL}/image-v2/exists`, {
							s3URL: this.AddSizePrefix(result.link, size),
						})
						.pipe(
							map((i) => of(i)),
							catchError(() => of(false)),
						);
				};
				return checkStatus().pipe(
					expand((res: boolean) => {
						if (res || timeCountFlag >= this.hardResetTimerLimit) {
							return EMPTY;
						}
						return checkStatus().pipe(delay(5 * 1000));
					}),
				);
			}),
			switchMap(() => {
				return of(result);
			}),
			catchError((err) => {
				console.log(err);
				return of(result);
			}),
		);
	}

	AddSizePrefix(urlOrig: string, size: ImageSizePrefix): string {
		let url = `${urlOrig}`;
		const ext = url.substring(url.lastIndexOf("."));
		return url.replace(ext, `-${size}${ext}`);
	}

	GetSignedURL(
		readOnlySignedURL: string,
		src: string,
		clientWidth?: number,
	): Observable<ISignedURL> {
		return this.http.post<ISignedURL>(`${environment.apiURL}${readOnlySignedURL}`, {
			src,
			clientWidth,
		});
	}

	OpenSearchImageDialog(): Observable<any> {
		return this.dialog
			.open(SearchImagesModalComponent, {
				width: "90vw",
				height: "90vh",
				autoFocus: false,
				hasBackdrop: true,
				restoreFocus: true,
				delayFocusTrap: true,
				enterAnimationDuration: "0ms",
			})
			.afterClosed();
	}
}
